import React, { useState } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import EndCallButtonIcon from '../Buttons/EndCallButton/EndCallButtonIcon';
import useRoomState from '../../hooks/useRoomState/useRoomState';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import { Grid, Dialog, DialogTitle, DialogActions, DialogContent, DialogContentText } from '@material-ui/core';
import ToggleAudioButtonIcon from '../Buttons/ToggleAudioButton/ToggleAudioButtonIcon';
import ToggleChatButtonIcon from '../Buttons/ToggleChatButton/ToggleChatButtonIcon';
import ToggleVideoButtonIcon from '../Buttons/ToggleVideoButton/ToggleVideoButtonIcon';
import ToggleParticipantsButtonIcon from '../Buttons/ToggleParticipantsButton/ToggleParticipantsButtonIcon';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      backgroundColor: 'rgba(24, 24, 27, 0.88)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      bottom: '24px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: 'auto',
      minWidth: '280px',
      borderRadius: '50px',
      boxShadow: '0 12px 36px rgba(0, 0, 0, 0.5)',
      border: '1px solid rgba(255, 255, 255, 0.15)',
      position: 'fixed',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '8px 20px',
      zIndex: 100,
      '& .MuiButton-root': {
        position: 'static !important',
        transform: 'none !important',
        top: 'auto !important',
        bottom: 'auto !important',
        verticalAlign: 'middle !important',
      },
      [theme.breakpoints.down('sm')]: {
        padding: '6px 14px',
        bottom: '16px',
      },
    },
    buttonRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      width: '100%',
    },
    border: {
      display: 'none',
    },
    dialogButton: {
      color: theme.palette.secondary.main,
      fontWeight: 'bold',
    },
  })
);

export default function MenuBar() {
  const classes = useStyles();
  const { room } = useVideoContext();
  const roomState = useRoomState();
  const isReconnecting = roomState === 'reconnecting';

  // State for Modal
  const [isEndCallModalOpen, setIsEndCallModalOpen] = useState(false);

  const handleOpenEndCallModal = () => {
    setIsEndCallModalOpen(true);
  };

  const handleCloseEndCallModal = () => {
    setIsEndCallModalOpen(false);
  };

  const handleEndCallForMe = () => {
    console.log('DEBUG [MenuBar]: Saliendo solo yo (End call for me). Manteniendo sesion Online.');
    handleCloseEndCallModal();
    const urlParams = new URLSearchParams(window.location.search);
    const isEntrenador = urlParams.get('entrenador') === 'true';
    const redirectUrl = isEntrenador ? 'https://app.walba.app/#!/home' : 'https://client.walba.app/app/tabs/schedule';

    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ action: 'redirect', url: redirectUrl }, '*');
    } else {
      window.location.href = redirectUrl;
    }
    room.disconnect();
  };

  const handleEndCallForEverybody = async () => {
    window.sessionStorage.setItem('meeting_ended', 'true');
    console.log('DEBUG [MenuBar]: Finalizando sesion para todos (End call for everybody)...');

    const pathSegments = window.location.pathname.split('/');
    const sessionId = (room && room.name) || pathSegments[2];
    const roomSid = room ? room.sid : null;

    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ type: 'END_SESSION', sessionId: sessionId }, '*');
      }
    } catch (e) {
      console.error('DEBUG [MenuBar]: postMessage error:', e);
    }

    try {
      const requests = [];
      if (roomSid) {
        requests.push(
          fetch('/api/complete-room', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ roomSid }),
          }).catch(err => console.error('Error complete-room:', err))
        );
      }

      if (sessionId) {
        console.log('DEBUG [MenuBar]: Finalizando sesion ID: ' + sessionId);
        requests.push(
          fetch('https://app.walba.app/api/endSessions/' + sessionId, {
            method: 'POST',
            mode: 'cors',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
          }).catch(err => console.error('Error endSessions fetch:', err))
        );
      }

      await Promise.all(requests);
      console.log('DEBUG [MenuBar]: Backend actualizado a Finished.');
    } catch (error) {
      console.error('DEBUG [MenuBar]: Error al sincronizar con el backend:', error);
    }

    handleCloseEndCallModal();
    room.disconnect();
  };

  const handleCloseSession = () => {
    room.disconnect();
  };


  const urlParams = new URLSearchParams(window.location.search);
  const isEntrenador = urlParams.get('entrenador') === 'true';

  return (
    <>
      <footer className={classes.container}>
        <div className={classes.buttonRow}>
          <ToggleAudioButtonIcon disabled={isReconnecting} />
          <ToggleVideoButtonIcon disabled={isReconnecting} />
          {process.env.REACT_APP_DISABLE_TWILIO_CONVERSATIONS !== 'true' && <ToggleChatButtonIcon />}
          <ToggleParticipantsButtonIcon />
          <EndCallButtonIcon onClick={isEntrenador ? handleOpenEndCallModal : handleCloseSession} />
        </div>
      </footer>

      <Dialog
        open={isEndCallModalOpen}
        onClose={handleCloseEndCallModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'End Call?'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">How would you like to leave the meeting?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEndCallForMe} color="primary">
            End call for me
          </Button>
          <Button onClick={handleEndCallForEverybody} className={classes.dialogButton} autoFocus>
            End call for everybody
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
